import { forwardRef, ComponentProps } from "react";

export type Props = Omit<ComponentProps<"textarea">, "onChange"> & {
  /** Field name for identification */
  name: string;
  /** Current value */
  value?: string;
  /** Change handler */
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Blur handler */
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Whether the field has an error (for styling and aria-invalid) */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** ID of the element that describes this input (typically error message) */
  "aria-describedby"?: string;
  /** ID of the element that labels this input */
  "aria-labelledby"?: string;
};

/**
 * Base textarea component with no styling.
 * Use this as the foundation for styled textarea components.
 */
export const TextareaBase = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      name,
      value,
      onChange,
      onBlur,
      error,
      disabled,
      className,
      required: _required, // Filtered to prevent HTML5 validation bubbles
      ...props
    },
    ref
  ) => {
    return (
      <textarea
        ref={ref}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={error || undefined}
        className={className}
        data-testid={`textarea-${name}`}
        {...props}
      />
    );
  }
);

TextareaBase.displayName = "TextareaBase";

